import type { Schema } from '@/api/client'

export type Monitor = Schema<'MonitorOut'>

/**
 * The intervals `MonitorIn` admits, as the three §6 names.
 *
 * 300–900 seconds is a *range* on the server, not an enumeration, so a value
 * outside these three is legal and `optionsFor` adds it rather than silently
 * showing the wrong one selected — a support script or a `psql` session can put
 * 450 on a row, and a control that then read as *Every 5 minutes* would be
 * lying about what is happening to the tenant's site.
 */
export const INTERVALS = [300, 600, 900] as const

/** How the monitor's three-way configuration is spelled in the one control. */
export const OFF = 'off'
export const PAUSED = 'paused'

/** Which option the control shows for the monitor in hand. */
export function valueOf(monitor: Monitor | null): string {
  if (monitor === null) return OFF
  if (!monitor.enabled) return PAUSED
  return String(monitor.interval_seconds)
}

/**
 * The intervals to offer, including one the server holds that this list does
 * not — see `INTERVALS` on why a fourth is possible and must not be hidden.
 */
export function optionsFor(monitor: Monitor | null): number[] {
  const stored = monitor?.interval_seconds
  if (stored === undefined || INTERVALS.includes(stored as (typeof INTERVALS)[number])) {
    return [...INTERVALS]
  }
  return [...INTERVALS, stored].sort((a, b) => a - b)
}

/**
 * An interval, as short as it can be said.
 *
 * *Every 15 min* rather than *Every 15 minutes*, because a `<select>` is as wide
 * as its widest option and the Sites table's last column has to fit the shell's
 * content width. Measured in a real browser at 1185px.
 */
export function describeInterval(seconds: number): string {
  return `Every ${String(Math.round(seconds / 60))} min`
}
